import { addCalendarDays } from "@common/helpers/date";
import { formatDateOnly } from "@helpers/date";
import type {
    DensityChartBucket,
    DensityChartBucketUnit,
} from "./densityBuckets.lib";
import {
    barStartDate,
    isMonday,
    isMonthStart,
    listDates,
    toBarIndex,
} from "./densityBuckets.lib";

export type DensityAxisTickKind = "major" | "minor";

export interface DensityAxisTick {
    /** The bar the tick sits under. */
    index: number;
    kind: DensityAxisTickKind;
    label?: string;
    /** A second line under `label`. */
    yearLabel?: string;
}

/** Labels closer than this, centre to centre, would run into each other. */
const MIN_LABEL_SPACING_PX = 32;

const isJanuary = (date: string) => date.slice(5, 7) === "01";
const QUARTER_MONTHS = new Set(["04", "07", "10"]);

function toMonthTick(
    bucket: DensityChartBucket,
    index: number,
): DensityAxisTick {
    if (isJanuary(bucket.start)) {
        return { index, kind: "major", label: bucket.start.slice(0, 4) };
    }
    return QUARTER_MONTHS.has(bucket.start.slice(5, 7))
        ? { index, kind: "minor", label: formatDateOnly(bucket.start, "MMM") }
        : { index, kind: "minor" };
}

function toCandidateTick({
    bucket,
    index,
    unit,
}: {
    bucket: DensityChartBucket;
    index: number;
    unit: DensityChartBucketUnit;
}): DensityAxisTick | null {
    if (unit === "month") {
        return toMonthTick(bucket, index);
    }
    const monthStart = bucket.dates.find(isMonthStart);
    if (monthStart) {
        return {
            index,
            kind: "major",
            label: formatDateOnly(monthStart, "MMM"),
            ...(isJanuary(monthStart) && { yearLabel: monthStart.slice(0, 4) }),
        };
    }
    return unit === "week" || isMonday(bucket.start)
        ? { index, kind: "minor" }
        : null;
}

/**
 * The tick of a month starting in the empty slot after today's bar, when the
 * last bar ends today; null otherwise.
 */
function toSlotTick({
    buckets,
    unit,
    endDate,
}: {
    buckets: DensityChartBucket[];
    unit: DensityChartBucketUnit;
    endDate: string;
}): DensityAxisTick | null {
    if (buckets.at(-1)?.end !== endDate) {
        return null;
    }
    const slotEnd = addCalendarDays(
        barStartDate({ index: toBarIndex({ date: endDate, unit }) + 2, unit }),
        -1,
    );
    const monthStart = listDates(addCalendarDays(endDate, 1), slotEnd).find(
        isMonthStart,
    );
    return monthStart
        ? toCandidateTick({
              bucket: {
                  start: monthStart,
                  end: monthStart,
                  dates: [monthStart],
              },
              index: buckets.length,
              unit,
          })
        : null;
}

/**
 * Drops labels too close to one already kept or to the left edge of the bars
 * in view: the ticks carrying the year first, so the axis names it, then the
 * rest left to right.
 */
function dropCrowdedLabels({
    ticks,
    spacingBars,
    visibleStart,
}: {
    ticks: DensityAxisTick[];
    spacingBars: number;
    visibleStart: number;
}): DensityAxisTick[] {
    const keptIndexes = new Set<number>();
    const isClear = (index: number) =>
        index + 0.5 - visibleStart >= spacingBars / 2 &&
        [...keptIndexes].every((kept) => Math.abs(index - kept) >= spacingBars);
    const labeledTicks = ticks.filter(({ label }) => label);
    for (const { index } of [
        ...labeledTicks.filter(({ yearLabel }) => yearLabel),
        ...labeledTicks.filter(({ yearLabel }) => !yearLabel),
    ]) {
        if (isClear(index)) {
            keptIndexes.add(index);
        }
    }
    return ticks.map((tick) =>
        !tick.label || keptIndexes.has(tick.index)
            ? tick
            : { index: tick.index, kind: tick.kind },
    );
}

/**
 * The ticks under the bars. Day bars: a major tick and month at each month
 * start, a minor tick on Mondays. Week bars: a major tick and month on the
 * week holding a month start, a minor tick on every other week. Both put the
 * year under January. Month bars: a major tick and the year on each January,
 * a minor tick on every other month, labeled each quarter. A month starting
 * in the slot after today gets its tick there too. Labels that would cross
 * the visible left edge are dropped instead of clipped.
 */
export function buildAxisTicks({
    buckets,
    unit,
    barWidth,
    endDate,
    visibleStart = -Infinity,
}: {
    buckets: DensityChartBucket[];
    unit: DensityChartBucketUnit;
    /** One bar's width in px, to drop labels that would collide; 0 keeps them all. */
    barWidth: number;
    /** Today, for the slot after its bar. */
    endDate?: string;
    /** Where the view starts, in bars from the first bucket's left. */
    visibleStart?: number;
}): DensityAxisTick[] {
    const candidates = [
        ...buckets.map((bucket, index) =>
            toCandidateTick({ bucket, index, unit }),
        ),
        endDate === undefined ? null : toSlotTick({ buckets, unit, endDate }),
    ].filter((tick) => tick !== null);
    const spacingBars = barWidth > 0 ? MIN_LABEL_SPACING_PX / barWidth : 0;
    return dropCrowdedLabels({ ticks: candidates, spacingBars, visibleStart });
}
